import React, { memo, useState, useEffect, useCallback } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useTranslation } from 'react-i18next'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import Grid from '@material-ui/core/Grid'
import Chip from '@material-ui/core/Chip'
import EditIcon from '@material-ui/icons/EditOutlined'
import TextField from '@material-ui/core/TextField'

import styles from './styles'
import { IValue, IValuesProps } from 'models'
import { useSetValuesMutation } from '../../../../../src/graphql/__generated__/graphql'

const useStyles = makeStyles(styles)

const Values: React.FunctionComponent<IValuesProps> = ({
	values,
	canEdit,
	updateUser,
}) => {
	const isMobile = useMediaQuery('@media (max-width:680px)')
	const [displayedValuesCount, setDisplayedValuesCount] = useState(
		isMobile ? values?.length : 5,
	)
	const [edit, setEdit] = useState(false)
	const [body, setBody] = useState('')
	const [loading, setLoading] = useState(false)
	const classes = useStyles()
	const { t } = useTranslation()
	const [success, setSuccess] = useState(false)
	const [updateMutationHandle] = useSetValuesMutation()
	const handleChange = useCallback(event => {
		setBody(event.target.value)
	}, [])
	const toggle = useCallback(() => {
		if (!edit) {
			setBody(values?.map(item => item.title).join(', ') || '')
		}
		setEdit(!edit)
	}, [edit, values])
	const handleViewAllValuesClick = () => {
		setDisplayedValuesCount(values?.length)
	}
	const displayViewAllValues: boolean =
		!!displayedValuesCount &&
		displayedValuesCount < (values?.length ?? 0) &&
		!isMobile

	const saveEdit = useCallback(() => {
		setEdit(false)
		setLoading(true)
	}, [])

	useEffect(() => {
		setDisplayedValuesCount(isMobile ? values?.length : 5)
	}, [isMobile, values])
	useEffect(() => {
		if (loading) {
			const mutate = async () => {
				try {
					await updateMutationHandle({
						variables: {
							values: body
								?.replace(/,\s*$/, '')
								?.split(',')
								?.map(item => item.trim().toLowerCase()),
							//Todo missing userID
							userId: '',
						},
					})
					setLoading(false)
					setSuccess(true)
					setTimeout(() => {
						setSuccess(false)
					}, 2000)
				} catch (error) {
					console.error(error)
					setLoading(false)
				}
			}
			mutate()
			updateUser()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loading, updateUser])

	return (
		<Grid
			container={true}
			direction="row"
			className={classes.valuesSection}
		>
			{isMobile && (
				<span className={classes.valuesLabel}>{t('Values')}: </span>
			)}
			<Grid
				container={true}
				direction="row"
				className={
					isMobile
						? classes.valuesOuterContainerMobile
						: classes.valuesOuterContainer
				}
			>
				<Grid
					container={true}
					direction="row"
					className={
						isMobile
							? classes.valuesInnerContainerMobile
							: classes.valuesInnerContainer
					}
				>
					{!isMobile && (
						<span className={classes.valuesLabel}>
							{t('Values')}:{' '}
						</span>
					)}
					{!edit &&
						values
							?.slice(0, displayedValuesCount)
							?.map((value: IValue, index: number) => {
								const isNotFirstItem: boolean = index !== 0

								return (
									<span
										key={`value-${index}`}
										className={`${classes.valueItem} ${
											isNotFirstItem
												? classes.valueSeparator
												: ''
										}`}
									>
										{value.title}
									</span>
								)
							})}
					{displayViewAllValues && (
						<span
							className={classes.viewAll}
							onClick={handleViewAllValuesClick}
						>
							{t('View all')}
						</span>
					)}
				</Grid>
				{canEdit && edit && (
					<Grid container={true} direction="row">
						<TextField
							id="standard-full-width"
							style={{ margin: '8px 0px' }}
							placeholder={t('your values, go here')}
							helperText="comma separated list of values"
							fullWidth
							margin="normal"
							InputLabelProps={{
								shrink: true,
							}}
							value={body}
							onChange={handleChange}
						/>
					</Grid>
				)}
			</Grid>
			{canEdit && !edit && (
				<>
					{!success && (
						<Chip
							onClick={toggle}
							icon={<EditIcon />}
							variant="outlined"
							color={'secondary'}
							clickable={true}
							classes={{
								label: classes.editIconLabel,
								root: classes.editRootElement,
							}}
						/>
					)}
					{success && (
						<div className={classes.saveSuccess}>{t('saved!')}</div>
					)}
				</>
			)}
			{canEdit && edit && (
				<div className={classes.saveCancelButtons}>
					<div className={classes.cancelEdit} onClick={toggle}>
						{t('cancel')}
					</div>
					<div className={classes.saveEdit} onClick={saveEdit}>
						{t('save')}
					</div>
				</div>
			)}
		</Grid>
	)
}

export default memo(Values)
